import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, tap, switchMap } from 'rxjs';
import { LoginRequest, LoginResponse, TokenPayload } from '../contracts/auth.interface';
import { jwtDecode } from 'jwt-decode';
import { RoleEnum } from '../../shared/contracts/role.interface';
import {User} from '../../users/contracts/user.interface';
import {WebSocketService} from '../../shared/services/webscoket.service';
import {TenantSettings} from '../../tenants/contracts/tenant_settings.interface';
import {map} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly TOKEN_KEY = 'authToken';
  private readonly REFRESH_TOKEN_KEY = 'refreshToken';
  private readonly USER_KEY = 'userData';
  private readonly USER_ROLE_KEY = 'userRole';
  private readonly API_URL = 'auth';
  private readonly USERS_API_URL = 'users';

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(this.hasValidToken());
  public isAuthenticated$ = this.isAuthenticatedSubject.asObservable();

  // Emits true when the app has successfully completed the initial setOnline call
  private onlineReadySubject = new BehaviorSubject<boolean>(false);
  public onlineReady$ = this.onlineReadySubject.asObservable();

  constructor(private http: HttpClient, private websocketService: WebSocketService) {
    // Check token validity on service initialization
    this.isAuthenticatedSubject.next(this.hasValidToken());
  }

  login(email: string, password: string): Observable<any> {
    const loginData: LoginRequest = { email, password };
    return this.http.post<LoginResponse>(`${this.API_URL}/login/json`, loginData)
      .pipe(
        tap(response => {
          // Store tokens
          localStorage.setItem(this.TOKEN_KEY, response.accessToken);
          if (response.refreshToken) {
            localStorage.setItem(this.REFRESH_TOKEN_KEY, response.refreshToken);
          }
          this.isAuthenticatedSubject.next(true);
        }),
        tap(() => this.websocketService.connect(this.getToken())),
        // After login, fetch user data
        switchMap(() => this.fetchCurrentUser())
      );
  }

  fetchCurrentUser(): Observable<User> {
    return this.http.get<User>(`${this.USERS_API_URL}/me`).pipe(
      tap(user => {
        this.storeUserData(user);
      }),
      switchMap(() => this.getSettings()),
      tap(settings => {
        // Se vuoi tenerlo anche in memoria oltre al localStorage, salva qui
        // this.settings = settings; (esempio, se aggiungi proprietà privata)
      }),
      map(() => {
        // Torna l'utente dopo che hai anche recuperato i settings
        return this.getCurrentUser()!;
      })
    );
  }

  private storeUserData(user: User): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    // Store role based on roleId
    const roleEnum = this.getRoleEnumFromId(user.roleId!);
    if (roleEnum) {
      localStorage.setItem(this.USER_ROLE_KEY, roleEnum);
    }
  }

  private getRoleEnumFromId(roleId: number): string | null {
    switch (roleId) {
      case 1:
        return RoleEnum.ADMIN;
      case 2:
        return RoleEnum.SUPERVISOR;
      case 3:
        return RoleEnum.OPERATOR;
      default:
        return null;
    }
  }

  logout(): void {
    const user = this.getCurrentUser();
    const userId = user?.id;

    if (userId) {
      this.http.post(`${this.USERS_API_URL}/${userId}/offline`, {})
        .subscribe({
          next: () => console.log('Offline OK'),
          error: (err) => console.error('Errore offline:', err)
        });
    }

    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.USER_ROLE_KEY);

    this.isAuthenticatedSubject.next(false);
    this.websocketService.closeConnection();
    // reset online readiness state
    this.setOnlineReady(false);
  }

  getCurrentUser(): User | null {
    const userData = localStorage.getItem(this.USER_KEY);
    if (!userData) {
      return null;
    }

    try {
      return JSON.parse(userData) as User;
    } catch (error) {
      this.logout()
      return null;
    }
  }

  getUserRole(): RoleEnum | null {
    const role = localStorage.getItem(this.USER_ROLE_KEY);
    if (!role) {
      return null;
    }

    return role as RoleEnum;
  }

  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  isLoggedIn(): boolean {
    return this.hasValidToken();
  }

  private hasValidToken(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    try {
      const decodedToken = jwtDecode<TokenPayload>(token);
      const currentTime = Date.now() / 1000;
      return decodedToken.exp > currentTime;
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  }

  getTokenPayload(): TokenPayload | null {
    const token = this.getToken();
    if (!token) {
      return null;
    }

    try {
      return jwtDecode<TokenPayload>(token);
    } catch (error) {
      console.error('Token decode error:', error);
      return null;
    }
  }

  getSettings(): Observable<TenantSettings> {
    return this.http.get<TenantSettings>(`${this.USERS_API_URL}/settings`).pipe(
      tap(settings => {
        localStorage.setItem('tenantSettings', JSON.stringify(settings));
      })
    );
  }

  // Allow components to signal when the initial online status is confirmed
  setOnlineReady(value: boolean): void {
    this.onlineReadySubject.next(value);
  }
}
